"use client";

/** Shared destination-page pieces used by the live homepage, cert hub, and study planner. */

import Image from "next/image";
import Link from "next/link";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  CheckCircle,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StudentOnboardingPreview } from "@/components/student-dashboard-preview";
import { StudyDayTimeRow } from "@/components/study-plans/am-pm-time-input";

export const PHOTO_HERO = "/images/home/study-group-circle.jpg";

/** Tiny inline stand-in so the cinema hero never paints black while the photo loads. */
export const PHOTO_HERO_BLUR =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABcQERQRDhcUEhQaGBcbIjklIh8fIkYyNSk5UkhXVVFIUE5bZoNvW2F8Yk5QcptzfIeLkpSSWG2grJ+OqoOPko3/2wBDARgaGiIeIkMlJUONXlBejY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY3/wAARCAAQABgDASIAAhEBAxEB/8QAFwAAAwEAAAAAAAAAAAAAAAAAAAQFA//EACMQAAIBAwQBBQAAAAAAAAAAAAECAwAREgQFEyExQVFhgfD/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABgRAQEBAQEAAAAAAAAAAAAAAAEAESFB/9oADAMBAAIRAxEAPwBds+NniB5Mwlj1b5rXQbkulcxzkGzBSxPf7qnoYAqu5JRW7yIH1U7UaCLkBiAkdrsWbrv1qYDXqT0u6JK0eCKUFlZsrYkmipcu1tJEs0I48fKE+T7iimkd8v/Z";

export const CERT_HERO_VIDEOS = ["/videos/certs-hero/loop.mp4"] as const;

export const CERT_HERO_POSTER = "/videos/certs-hero/poster.jpg";

export const HOME_CHIPS = ["NCLEX-RN", "MCAT", "USMLE", "ATI TEAS", "PMP", "SECURITY+", "CFA"] as const;

export const POPULAR = [
  { label: "PMP", name: "PMP", org: "PMI", fact: "180 questions · $405" },
  { label: "CISSP", name: "CISSP", org: "ISC2", fact: "Adaptive test · $749" },
  { label: "NCLEX-RN", name: "NCLEX-RN", org: "NCSBN", fact: "CAT exam · $200" },
  { label: "AWS Solutions Architect", name: "AWS Solutions Architect", org: "Amazon Web Services", fact: "65 questions · $150" },
  { label: "Security+", name: "CompTIA Security+", org: "CompTIA", fact: "90 questions · $404" },
] as const;

export const GROUPS = [
  { name: "PMP May Cohort", meta: "Public group · 24 members" },
  { name: "CISSP Study Group", meta: "Private group · 1,248 members" },
  { name: "AWS SAA Study Circle", meta: "Public group · 31 members" },
] as const;

export const TUTORS = [
  { name: "Darius J.", certs: "CISSP · CCSP", rate: "$95/hr", initials: "DJ" },
  { name: "Sarah M.", certs: "CISSP · Security+", rate: "$80/hr", initials: "SM" },
  { name: "James P.", certs: "PMP · CAPM", rate: "$70/hr", initials: "JP" },
] as const;

export const STEPS = [
  {
    n: "01",
    title: "Find your certification exam",
    body: "Explore exam details and preparation guidance, then connect with people working toward the same goal.",
  },
  {
    n: "02",
    title: "Find your people",
    body: "Join a study group for your certification or start one of your own. Share resources, compare study strategies, and keep each other accountable.",
  },
  {
    n: "03",
    title: "Study together. Keep moving forward.",
    body: "Share resources, ask questions, follow your progress, and get help from tutors who've already earned the certification.",
  },
] as const;

export const BENEFIT =
  "A certification signals skill, opens doors to new roles, and gives you a community while you prepare — so you are not studying in the dark.";

export const LONELY =
  "Preparing for a certification exam can feel lonely. CertLynx connects you with people preparing for the same exam — so you can study together, stay accountable, share what you’re learning, and keep moving toward exam day.";

export const PLAN_HELP =
  "Tell us your exam date, what you're studying, and when you're available. CertLynx will create a personalized study schedule and give you a downloadable calendar invite, so your study time becomes part of your calendar — not just another plan you have to remember.";

const PLAN_DAYS = [
  { label: "Sunday", short: "Sun", on: false, hrs: "", start: "", end: "" },
  { label: "Monday", short: "Mon", on: true, hrs: "2", start: "18:00", end: "20:00" },
  { label: "Tuesday", short: "Tue", on: true, hrs: "2", start: "18:00", end: "20:00" },
  { label: "Wednesday", short: "Wed", on: true, hrs: "2", start: "18:00", end: "20:00" },
  { label: "Thursday", short: "Thu", on: true, hrs: "2", start: "18:00", end: "20:00" },
  { label: "Friday", short: "Fri", on: true, hrs: "2", start: "18:00", end: "20:00" },
  { label: "Saturday", short: "Sat", on: false, hrs: "", start: "", end: "" },
] as const;

const PLAN_SECTIONS = [
  { name: "Network Security", hours: 8 },
  { name: "Firewalls", hours: 4 },
  { name: "Access Control", hours: 6 },
] as const;

const WEEK_HOURS = [6, 8, 10, 12, 14, 16, 18, 20] as const;
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"] as const;

const WEEK_BLOCKS = [
  { day: 0, startHour: 8, label: "Network Security", tone: "bg-blue-100 text-blue-800" },
  { day: 0, startHour: 14, label: "Practice Questions", tone: "bg-emerald-100 text-emerald-800" },
  { day: 1, startHour: 10, label: "Firewalls", tone: "bg-amber-100 text-amber-900" },
  { day: 1, startHour: 16, label: "Practice Questions", tone: "bg-emerald-100 text-emerald-800" },
  { day: 2, startHour: 8, label: "Access Control", tone: "bg-violet-100 text-violet-800" },
  { day: 2, startHour: 14, label: "VPNs", tone: "bg-amber-100 text-amber-900" },
  { day: 3, startHour: 10, label: "Practice Exam", tone: "bg-blue-100 text-blue-800" },
  { day: 3, startHour: 16, label: "Review Notes", tone: "bg-violet-100 text-violet-800" },
  { day: 4, startHour: 8, label: "Review Weak Areas", tone: "bg-emerald-100 text-emerald-800" },
  { day: 4, startHour: 14, label: "Light Review", tone: "bg-amber-100 text-amber-900" },
] as const;

function hourLabel(h: number) {
  if (h === 12) return "12 PM";
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

export function Quote() {
  return (
    <blockquote className="text-lg italic text-gray-600 leading-snug">
      &ldquo;Becoming is better than being.&rdquo;
      <span className="not-italic block mt-2 text-xs font-semibold tracking-wide uppercase text-gray-400">
        Carol Dweck
      </span>
    </blockquote>
  );
}

/** MH: type lives ON the photograph. Full-bleed banner sized by its content. */
export function Cinema({
  src,
  children,
  height = "min-h-[32rem]",
  placeholder,
}: {
  src: string;
  children: ReactNode;
  height?: string;
  placeholder?: string;
}) {
  return (
    <section className={`relative w-full overflow-hidden bg-[#3d3428] ${height}`}>
      {/* LQIP + warm fallback paint immediately; full photo is a static file (no optimizer). */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 bg-cover bg-center scale-110 blur-xl"
        style={{
          backgroundColor: "#3d3428",
          backgroundImage: placeholder ? `url(${placeholder})` : undefined,
        }}
      />
      {/* Native img: fetchPriority + static URL so refresh does not wait on /_next/image. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        width={1024}
        height={682}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 z-[1] h-full w-full object-cover object-[center_30%]"
      />
      <div className="absolute inset-0 z-[2] bg-black/45 pointer-events-none" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 sm:py-20">
        {children}
      </div>
    </section>
  );
}

const HERO_VIDEO_CLASS =
  "hero-loop-video absolute inset-0 z-[1] h-full w-full object-cover pointer-events-none";

function armHeroVideo(el: HTMLVideoElement) {
  el.muted = true;
  el.defaultMuted = true;
  el.autoplay = true;
  el.loop = true;
  el.playsInline = true;
  el.disablePictureInPicture = true;
  el.setAttribute("playsinline", "true");
  el.setAttribute("webkit-playsinline", "true");
  el.setAttribute("muted", "");
  el.removeAttribute("controls");
}

/** Cinema hero: looping video in the background, poster underneath, no controls. */
export function CinemaLoop({
  sources = CERT_HERO_VIDEOS,
  poster = CERT_HERO_POSTER,
  children,
  height = "min-h-[520px]",
}: {
  sources?: readonly string[];
  poster?: string;
  children: ReactNode;
  height?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = sources[0];

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    armHeroVideo(el);
    const play = () => {
      el.muted = true;
      el.defaultMuted = true;
      if (el.ended) el.currentTime = 0;
      void el.play().catch(() => {});
    };
    const events = ["loadeddata", "canplay", "canplaythrough", "playing"] as const;
    events.forEach((name) => el.addEventListener(name, play));
    play();
    const retry = window.setInterval(() => {
      if (!el.paused) {
        window.clearInterval(retry);
        return;
      }
      play();
    }, 1500);
    const onVis = () => {
      if (document.visibilityState === "visible") play();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      events.forEach((name) => el.removeEventListener(name, play));
      window.clearInterval(retry);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [src]);

  return (
    <section className={`relative z-0 ${height} overflow-hidden bg-black`}>
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {/* Native img so the poster stays behind the video (next/image fill can stack on top). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={poster}
          alt=""
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className={HERO_VIDEO_CLASS}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          tabIndex={-1}
        />
        <div className="absolute inset-0 z-[2] bg-black/55 pointer-events-none" />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 sm:py-24">
        {children}
      </div>
    </section>
  );
}

/** WA: type left, photo right, almost no chrome. */
export function QuietSplit({
  src,
  children,
  photoLeft = false,
}: {
  src: string;
  children: ReactNode;
  photoLeft?: boolean;
}) {
  return (
    <section className="grid lg:grid-cols-2 min-h-[560px] bg-white">
      <div className={`relative min-h-[320px] ${photoLeft ? "lg:order-1" : "lg:order-2"}`}>
        <Image src={src} alt="" fill className="object-cover object-center" />
      </div>
      <div
        className={`flex flex-col justify-center px-8 sm:px-14 py-16 ${
          photoLeft ? "lg:order-2" : "lg:order-1"
        }`}
      >
        {children}
      </div>
    </section>
  );
}

export function Finder({
  placeholder,
  onPick,
  className = "",
}: {
  placeholder: string;
  onPick?: (q: string) => void;
  className?: string;
}) {
  return (
    <div className={`flex items-stretch bg-white overflow-hidden border border-gray-200 max-w-xl ${className || "rounded-md"}`}>
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder={placeholder}
          className="border-0 shadow-none focus-visible:ring-0 h-12 pl-10 rounded-none"
        />
      </div>
      <Button
        className="rounded-none h-12 px-6 bg-blue-600 hover:bg-blue-700"
        onClick={() => onPick?.("CISSP")}
      >
        Search
      </Button>
    </div>
  );
}

export function ChipRow({
  onPick,
  light = false,
}: {
  onPick: (q: string) => void;
  light?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      <span className={`text-sm ${light ? "text-white/70" : "text-gray-500"}`}>
        Popular:
      </span>
      {HOME_CHIPS.map((label) => (
        <button
          key={label}
          type="button"
          onClick={() => onPick(label)}
          className={
            light
              ? "text-sm text-white/90 underline-offset-4 hover:underline"
              : "text-sm font-medium text-blue-700 hover:underline"
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** McGraw Hill “Our Culture”: intro left, L-shaped 1+2 cards right. */
export function CultureSteps({
  photo,
  visual,
  heading,
  body,
  cta,
  ctaHref,
  onCta,
}: {
  photo?: string;
  visual?: ReactNode;
  heading: string;
  body: string;
  cta: string;
  ctaHref?: string;
  onCta?: () => void;
}) {
  const [first, second, third] = STEPS;
  return (
    <section className="bg-gray-100 py-16">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.55fr)] gap-10 items-start">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
            {heading}
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">{body}</p>
          <Button
            className="mt-6 bg-blue-600 hover:bg-blue-700"
            onClick={ctaHref ? undefined : onCta}
            asChild={!!ctaHref}
          >
            {ctaHref ? <Link href={ctaHref}>{cta}</Link> : cta}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 bg-white rounded-xl shadow-md overflow-hidden flex min-h-[240px]">
            <div className="p-6 flex-1 min-w-0 flex flex-col justify-center">
              <p className="text-xs font-semibold text-blue-600">{first.n}</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{first.title}</p>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{first.body}</p>
            </div>
            <div className="relative w-[48%] min-h-[240px] hidden sm:block shrink-0 bg-gray-50">
              {visual ??
                (photo ? (
                  <Image src={photo} alt="" fill className="object-cover object-center" />
                ) : (
                  <StudentOnboardingPreview />
                ))}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <p className="text-xs font-semibold text-blue-600">{second.n}</p>
            <p className="text-lg font-bold text-gray-900 mt-1">{second.title}</p>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{second.body}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <p className="text-xs font-semibold text-blue-600">{third.n}</p>
            <p className="text-lg font-bold text-gray-900 mt-1">{third.title}</p>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{third.body}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function QuoteBand() {
  return (
    <section className="bg-blue-600 py-14 px-6 text-center">
      <blockquote className="text-2xl sm:text-3xl font-semibold text-white max-w-3xl mx-auto leading-snug">
        You&rsquo;re not just studying for an exam. You&rsquo;re building what
        comes next.
      </blockquote>
    </section>
  );
}

export function StepRail() {
  return (
    <ol className="space-y-8">
      {STEPS.map((s) => (
        <li key={s.n} className="flex gap-5">
          <span className="text-sm font-semibold text-blue-600 w-8 shrink-0 pt-1">
            {s.n}
          </span>
          <div>
            <p className="text-xl font-semibold text-gray-900">{s.title}</p>
            <p className="text-gray-600 mt-1 leading-relaxed">{s.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function ExamList({ onPick }: { onPick: (q: string) => void }) {
  return (
    <ul className="divide-y divide-gray-200">
      {POPULAR.map((c) => (
        <li key={c.label}>
          <button
            type="button"
            onClick={() => onPick(c.label)}
            className="w-full text-left py-4 flex items-baseline justify-between gap-4 hover:text-blue-700"
          >
            <span>
              <span className="font-semibold text-gray-900">{c.label}</span>
              <span className="text-sm text-gray-500 ml-2">{c.org}</span>
            </span>
            <span className="text-sm text-gray-500">{c.fact}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function GroupLines() {
  return (
    <ul className="divide-y divide-gray-200">
      {GROUPS.map((g) => (
        <li key={g.name} className="py-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-gray-900">{g.name}</p>
            <p className="text-sm text-gray-500">{g.meta}</p>
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
            Join
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function TutorLines() {
  return (
    <ul className="divide-y divide-gray-200">
      {TUTORS.map((t) => (
        <li key={t.name} className="py-4 flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">
            {t.initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 flex items-center gap-1">
              {t.name}
              <BadgeCheck className="h-3.5 w-3.5 text-blue-600" />
            </p>
            <p className="text-sm text-gray-500">{t.certs}</p>
          </div>
          <span className="text-sm font-semibold text-blue-700">{t.rate}</span>
        </li>
      ))}
    </ul>
  );
}

export function TrustRow() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-600">
      {["Identity verified", "Credentials reviewed", "Background checked"].map(
        (label) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
            {label}
          </span>
        )
      )}
    </div>
  );
}

export function WeekCalendar({ className = "" }: { className?: string }) {
  const rowH = 56;
  const colCount = WEEK_DAYS.length;
  const rowCount = WEEK_HOURS.length;

  return (
    <div
      className={`bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-5 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-900">This week</p>
        <div className="flex items-center gap-1">
          <span className="h-8 w-8 rounded-md border border-gray-200 inline-flex items-center justify-center">
            <ChevronLeft className="h-4 w-4 text-gray-500" />
          </span>
          <span className="h-8 w-8 rounded-md border border-gray-200 inline-flex items-center justify-center">
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </span>
        </div>
      </div>
      <div className="flex">
        <div className="w-12 shrink-0" style={{ paddingTop: 28 }}>
          {WEEK_HOURS.map((hour) => (
            <div
              key={hour}
              className="text-[10px] text-gray-400 text-right pr-2 leading-none -translate-y-1.5"
              style={{ height: rowH }}
            >
              {hourLabel(hour)}
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="grid mb-1"
            style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0,1fr))` }}
          >
            {WEEK_DAYS.map((day) => (
              <div key={day} className="text-center pb-2">
                <span className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  {day}
                </span>
              </div>
            ))}
          </div>
          <div
            className="relative overflow-hidden rounded-lg border border-gray-200 bg-white"
            style={{ height: rowH * rowCount }}
          >
            <div
              className="grid h-full"
              style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0,1fr))` }}
            >
              {WEEK_DAYS.map((day, col) => (
                <div key={day} className="flex flex-col min-w-0">
                  {WEEK_HOURS.map((hour, row) => {
                    const block = WEEK_BLOCKS.find(
                      (b) => b.day === col && b.startHour === hour
                    );
                    return (
                      <div
                        key={`${day}-${hour}`}
                        className={`flex-1 min-h-0 p-1 ${
                          col < colCount - 1 ? "border-r border-gray-200" : ""
                        } ${row < rowCount - 1 ? "border-b border-gray-200" : ""}`}
                      >
                        {block ? (
                          <div
                            className={`h-full rounded-lg px-2 py-1.5 text-[11px] font-semibold leading-tight overflow-hidden ${block.tone}`}
                          >
                            {block.label}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlannerForm({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      <div className="flex items-center justify-between">
        <p className="text-lg font-semibold text-gray-900">Study Planner</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
        >
          Reset All
        </Button>
      </div>
      <div className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Your exam
        </p>
      <div className="space-y-2">
        <Label>Exam Date</Label>
        <Input type="date" defaultValue="2026-08-15" />
      </div>
      <div className="space-y-2">
        <Label>Certification</Label>
        <Input defaultValue="CISSP" placeholder="Select certification" />
      </div>
      <div className="space-y-2 border border-gray-200 rounded-md p-4">
        <Label>Exam Sections or Topics</Label>
        <div className="flex gap-2 mt-2 mb-2">
          <Input placeholder="Section name" className="flex-1" />
          <Input placeholder="Hours" className="w-24" />
          <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white">
            Add
          </Button>
        </div>
        <ul className="m-0 p-0 list-none">
          {PLAN_SECTIONS.map((section) => (
            <li key={section.name} className="flex items-center gap-2 mb-1">
              <span className="flex-1 text-sm">{section.name}</span>
              <span className="w-16 text-sm">{section.hours} hrs</span>
              <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700">
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </div>
      </div>
      <div className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Your availability
        </p>
      <div className="space-y-2">
        <Label>Study Days of the Week</Label>
        <div className="grid grid-cols-7 gap-1.5 mt-2">
          {PLAN_DAYS.map((day) => (
            <Button
              key={day.label}
              type="button"
              size="sm"
              variant={day.on ? "default" : "outline"}
              aria-label={day.label}
              className={`min-w-0 px-0 overflow-hidden ${
                day.on ? "bg-blue-600 hover:bg-blue-700 text-white" : ""
              }`}
            >
              {day.short}
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Study Hours and Times</Label>
        <p className="text-xs text-gray-500">
          Set how long you study and the exact start and end time for each day.
          Calendar holds use these times so the block shows as busy.
        </p>
        <div className="mt-2 space-y-2">
          {PLAN_DAYS.map((day) => (
            <StudyDayTimeRow
              key={day.label}
              label={day.label}
              enabled={day.on}
              hours={day.hrs}
              onHoursChange={() => {}}
              start={day.start}
              onStartChange={() => {}}
              end={day.end}
              onEndChange={() => {}}
            />
          ))}
        </div>
      </div>
      </div>
      <div className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Your plan
        </p>
      <div className="space-y-2">
        <Label>Email Address</Label>
        <Input type="email" defaultValue="you@example.com" placeholder="your.email@example.com" />
        <p className="text-xs text-gray-500 leading-relaxed">
          We&apos;ll email you a PDF of this plan. Use Add to Calendar on this page
          to download the invite. This is a one-time message — we don&apos;t send
          recurring study reminders.
        </p>
      </div>
      <Button type="button" className="w-full bg-blue-600 hover:bg-blue-700">
        Generate Study Plan
      </Button>
      </div>
    </div>
  );
}

export function PlanIntro({ light = false }: { light?: boolean }) {
  const steps = [
    "Choose your exam",
    "Enter your exam date",
    "Add topics",
    "Set your availability",
    "Generate your plan",
  ];
  return (
    <>
      <h1
        className={`text-4xl font-bold tracking-tight ${
          light ? "text-white" : "text-gray-900"
        }`}
      >
        Create a Study Schedule Around Your Exam Date
      </h1>
      <p
        className={`mt-4 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto ${
          light ? "text-white/85" : "text-gray-600"
        }`}
      >
        Build a personalized study schedule around your exam date. Choose what
        you&apos;re preparing for, enter your test date and available study
        time, and CertLynx will organize your topics into a schedule you can
        add to your calendar and track as you make progress.
      </p>
      <ol
        className={`mt-6 flex flex-nowrap items-center justify-center gap-x-4 overflow-x-auto text-sm ${
          light ? "text-white/90" : "text-gray-800"
        }`}
      >
        {steps.map((step, i) => (
          <li key={step} className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <span
              className={`h-6 w-6 rounded-full text-xs font-semibold flex items-center justify-center shrink-0 ${
                light ? "bg-white text-blue-700" : "bg-blue-600 text-white"
              }`}
            >
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </>
  );
}
